import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Divider, Result, Tag, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  useGetOrgInvitationByTokenId,
  useUpdateInvitationStatus,
} from '@/shared/api';
import { useRouterContext } from '@/shared/router';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Col } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';
import { PageLayout } from '@/widgets/page-layout';

const { Title, Paragraph } = Typography;

export const OrganizationInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { isAuthenticated } = useAuth0();
  const { activeOrganization } = useRouterContext();

  const [invitationAccepted, setInvitationAccepted] = useState(false);

  const { data: invitationDetailsData, isLoading: invitationDetailsLoading } =
    useGetOrgInvitationByTokenId<any>(
      {
        queryParams: {
          token: token!,
        },
      },
      {
        enabled: Boolean(token && isAuthenticated),
        onError: (error: any) => {
          if (error.stack.error === 'ForbiddenInvitation') {
            navigate('/dashboard', { replace: true });
            return;
          }
          if (
            error.stack.statusCode === 400 ||
            error.stack.statusCode === 401 ||
            error.stack.statusCode === 403 ||
            error.stack.statusCode === 404
          ) {
            navigate('/dashboard/404', { replace: true });
          }
        },
      },
    );

  const { mutate: acceptInvitation, isLoading: acceptInvitationLoading } =
    useUpdateInvitationStatus();

  const handleInvitationAccept = () => {
    try {
      acceptInvitation(
        {
          pathParams: {
            organizationId: activeOrganization?.id!,
            invitationId: invitationDetailsData?.id,
          },
          body: {
            status: 'accept',
          },
        },
        {
          onSuccess: () => {
            setInvitationAccepted(true);
          },
        },
      );
    } catch (error) {
      captureError(error, {
        module: 'organization_invitation_on_error_accept_invitation',
        action: 'accept_invitation',
      });
    }
  };

  if (invitationDetailsLoading) {
    return null;
  }

  return (
    <PageLayout
      headerContent={
        <img
          src="https://suresteps-public.s3.amazonaws.com/wordmark.svg"
          style={{ height: 25 }}
        />
      }
    >
      <Wrapper justifyContent="center">
        <>
          {invitationAccepted ? (
            <Result
              status="success"
              title="Joined organization successfully"
              subTitle={
                <span style={{ color: themeConfig[theme].colors.text }}>
                  Use the button below to go to dashboard and explore joined
                  organization.
                </span>
              }
              extra={[
                <Button onClick={() => navigate('/dashboard')}>
                  Go to dashboard
                </Button>,
              ]}
            />
          ) : (
            <>
              <Title level={2}>Join organization</Title>
              <Paragraph style={{ fontSize: 15, textAlign: 'center' }}>
                <strong>{invitationDetailsData?.invitedByEmail}</strong> invited
                you to join their organization{' '}
                <Tag style={{ fontWeight: 700, fontSize: 15 }}>
                  {invitationDetailsData?.organizationName}
                </Tag>
              </Paragraph>
              <Col
                justifyContent="center"
                style={{ flexWrap: 'wrap', marginTop: 30 }}
              >
                <Button
                  type="primary"
                  size="large"
                  loading={acceptInvitationLoading}
                  onClick={handleInvitationAccept}
                >
                  Join organization
                </Button>
                <Divider plain>Or</Divider>
                <Button onClick={() => navigate('/dashboard')}>
                  Go back to dashboard
                </Button>
              </Col>
            </>
          )}
        </>
      </Wrapper>
    </PageLayout>
  );
};

const Wrapper = styled(Col)`
  height: 100%;
  padding: 0 20px;
`;
